import { useEffect, useState } from 'react';
import { getAdminUniforms, updateUniformRequest, GetAdminUniformsOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Shirt } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

type Req = GetAdminUniformsOutputType['requests'][0];
type Alloc = GetAdminUniformsOutputType['allocations'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d.includes('T') ? d : d + 'T00:00:00'), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

const statusColor: Record<string, string> = {
  Pending: 'bg-amber-500/10 text-amber-600',
  Ordered: 'bg-primary/10 text-primary',
  Fulfilled: 'bg-primary text-primary-foreground',
  Rejected: 'bg-destructive/10 text-destructive',
};

export default function UniformAdminTab() {
  const [requests, setRequests] = useState<Req[]>([]);
  const [allocations, setAllocations] = useState<Alloc[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'requests' | 'allocations'>('requests');
  const [selectedReq, setSelectedReq] = useState<Req | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const load = () => {
    getAdminUniforms({}).then(d => { setRequests(d.requests); setAllocations(d.allocations); })
      .catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleUpdate = async () => {
    if (!selectedReq || !newStatus) return;
    setUpdating(true);
    try {
      await updateUniformRequest({ id: selectedReq.id, status: newStatus as any, orderNotes: notes || undefined });
      toast.success('Request updated');
      setSelectedReq(null);
      load();
    } catch { toast.error('Failed to update'); }
    finally { setUpdating(false); }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  const pending = requests.filter(r => r.status === 'Pending').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Shirt className="w-4 h-4 text-primary" /> Uniform Management
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">{pending} pending requests · {allocations.length} allocations</p>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-border">
        {(['requests', 'allocations'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize flex items-center gap-1 ${tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t === 'requests' ? `Requests (${requests.length})` : `Allocations (${allocations.length})`}
            {t === 'requests' && pending > 0 && <span className="bg-amber-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{pending}</span>}
          </button>
        ))}
      </div>

      {tab === 'requests' && (
        <div className="border border-border rounded-lg divide-y divide-border">
          {requests.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No uniform requests yet</div>
          ) : requests.map(r => (
            <div key={r.id} className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{r.employeeName}</p>
                <p className="text-xs text-muted-foreground">{r.itemType} — Size {r.size} · {fmtDate(r.requestedAt)}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${statusColor[r.status] || statusColor.Pending}`}>{r.status}</span>
              {r.status !== 'Fulfilled' && r.status !== 'Rejected' && (
                <Button size="sm" variant="outline" onClick={() => { setSelectedReq(r); setNewStatus(r.status); setNotes(r.orderNotes || ''); }}>
                  Update
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'allocations' && (
        <div className="border border-border rounded-lg divide-y divide-border">
          {allocations.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No allocations yet</div>
          ) : allocations.map(a => (
            <div key={a.id} className="px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">{a.employeeName}</p>
                <p className="text-xs text-muted-foreground">{a.itemType} — Size {a.size}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-foreground">{a.quantity}×</p>
                <p className="text-xs text-muted-foreground">{a.lastIssued ? fmtDate(a.lastIssued) : '—'}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selectedReq} onOpenChange={open => !open && setSelectedReq(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update Uniform Request</DialogTitle></DialogHeader>
          {selectedReq && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-foreground">
                <strong>{selectedReq.employeeName}</strong> — {selectedReq.itemType} Size {selectedReq.size}
              </p>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Ordered">Ordered</SelectItem>
                    <SelectItem value="Fulfilled">Fulfilled</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Order Notes (optional)</label>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes..." className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground" />
              </div>
              {newStatus === 'Fulfilled' && (
                <p className="text-xs text-primary bg-primary/5 px-3 py-2 rounded">
                  Marking as Fulfilled will automatically update the employee's allocation quantity.
                </p>
              )}
              <Button onClick={handleUpdate} disabled={updating} className="w-full">
                {updating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</> : 'Save Update'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
